'use client'

import { memo, type ReactNode } from 'react'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { cn } from '@/lib/utils'

// Shared easing: snappy CRT feel
const REVEAL_EASE = [0.16, 1, 0.3, 1] as const

const revealVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0 },
}

const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const slideLeftVariants: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0 },
}

const slideRightVariants: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0 },
}

const scaleVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
}

const blurVariants: Variants = {
  hidden: { opacity: 0, y: 28, filter: 'blur(12px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
}

/** Pixel pop: slight overshoot like a cartridge slotting in */
const popVariants: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.88 },
  visible: { opacity: 1, y: 0, scale: 1 },
}

/** Webflow /pay-style deep rise: longer travel than `up` */
const riseVariants: Variants = {
  hidden: { opacity: 0, y: 96 },
  visible: { opacity: 1, y: 0 },
}

/** Icon / badge pop from zero scale (pay page scale3d(0,0,1)) */
const popIconVariants: Variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: { opacity: 1, scale: 1 },
}

type RevealVariant =
  | 'up'
  | 'fade'
  | 'left'
  | 'right'
  | 'scale'
  | 'blur'
  | 'pop'
  | 'rise'
  | 'pop-icon'

const VARIANT_MAP: Record<RevealVariant, Variants> = {
  up: revealVariants,
  fade: fadeVariants,
  left: slideLeftVariants,
  right: slideRightVariants,
  scale: scaleVariants,
  blur: blurVariants,
  pop: popVariants,
  rise: riseVariants,
  'pop-icon': popIconVariants,
}

export interface NinxlScrollRevealProps {
  children: ReactNode
  className?: string
  /** Stagger delay in seconds */
  delay?: number
  duration?: number
  variant?: RevealVariant
}

/** Scroll-triggered reveal: fires once when element enters viewport */
export const NinxlScrollReveal = memo(function NinxlScrollReveal({
  children,
  className,
  delay = 0,
  duration = 0.7,
  variant = 'up',
}: NinxlScrollRevealProps) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return <div className={cn(className)}>{children}</div>
  }

  const transition =
    variant === 'pop' || variant === 'pop-icon'
      ? { type: 'spring' as const, stiffness: 280, damping: 22, delay }
      : {
          duration: variant === 'rise' ? Math.max(duration, 0.9) : duration,
          delay,
          ease: REVEAL_EASE,
        }

  return (
    <motion.div
      className={cn(className)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-64px', amount: 0.15 }}
      variants={VARIANT_MAP[variant]}
      transition={transition}
    >
      {children}
    </motion.div>
  )
})

export interface NinxlStaggerRevealProps {
  children: ReactNode
  className?: string
  /** Delay between each child in seconds */
  stagger?: number
}

/** Staggered children reveal: wrap each child in a motion.div */
export const NinxlStaggerReveal = memo(function NinxlStaggerReveal({
  children,
  className,
  stagger = 0.1,
}: NinxlStaggerRevealProps) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-64px', amount: 0.12 }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: 0.06 } },
      }}
    >
      {children}
    </motion.div>
  )
})

/** Child item for NinxlStaggerReveal */
export const NinxlStaggerItem = memo(function NinxlStaggerItem({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <motion.div
      className={cn('ninxl-stagger-card', className)}
      variants={{
        hidden: { opacity: 0, y: 28, scale: 0.96 },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: 0.6, ease: REVEAL_EASE },
        },
      }}
      whileHover={{ y: -4, transition: { duration: 0.25 } }}
    >
      {children}
    </motion.div>
  )
})

export interface NinxlGrowBarProps {
  className?: string
  /** Delay before the bar grows in */
  delay?: number
  duration?: number
}

/**
 * baby: Scroll-linked underline / progress bar: width grows 0 → 100%
 * (Webflow /pay used width:0% → full). Prefer scaleX for GPU.
 */
export const NinxlGrowBar = memo(function NinxlGrowBar({
  className,
  delay = 0.08,
  duration = 0.85,
}: NinxlGrowBarProps) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return (
      <div
        className={cn(
          'h-0.5 w-full bg-[color:var(--ninxl-brand-teal)]',
          className,
        )}
        aria-hidden
      />
    )
  }

  return (
    <motion.div
      className={cn(
        'h-0.5 w-full origin-left bg-[color:var(--ninxl-brand-teal)]',
        className,
      )}
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: '-48px', amount: 0.4 }}
      transition={{ duration, delay, ease: REVEAL_EASE }}
      aria-hidden
    />
  )
})
